(() => {
  if (customElements.get("collection-filters")) return;

  function debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  class CollectionFilters extends HTMLElement {
    static filterData = [];
    static searchParamsInitial = window.location.search.slice(1);
    static searchParamsPrev = window.location.search.slice(1);
    static historyBound = false;

    constructor() {
      super();
      this._onChange = this._onChange.bind(this);
      this._onDebouncedInput = debounce(this._onChange, 600);
      this._onClick = this._onClick.bind(this);
      this._onPopState = this._onPopState.bind(this);
      this._onPanelInput = this._onPanelInput.bind(this);
      this._onFiltersViewportChange = this._onFiltersViewportChange.bind(this);
      this._onSortViewportChange = this._onSortViewportChange.bind(this);
      this._onInfiniteIntersect = this._onInfiniteIntersect.bind(this);
      this._filtersPanel = null;
      this._filtersOverlay = null;
      this._filtersPlaceholder = null;
      this._filtersPortaled = false;
      this._sortMedia = null;
      this._infiniteObserver = null;
      this._loadingMore = false;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      if (!this.sectionId) return;

      this.paginationMode = this.dataset.paginationMode || "pagination";

      this.addEventListener("change", this._onChange);
      this.addEventListener("input", this._onPanelInput);
      this.addEventListener("click", this._onClick);

      this._sortMedia = window.matchMedia("(min-width: 990px)");
      this._sortMedia.addEventListener("change", this._onSortViewportChange);
      this._syncSortControls();
      this._setupPaginationMode();

      if (!CollectionFilters.historyBound) {
        window.addEventListener("popstate", this._onPopState);
        CollectionFilters.historyBound = true;
      }
    }

    disconnectedCallback() {
      this.removeEventListener("change", this._onChange);
      this.removeEventListener("input", this._onPanelInput);
      this.removeEventListener("click", this._onClick);
      this._sortMedia?.removeEventListener("change", this._onSortViewportChange);
      this._sortMedia = null;
      this._unbindFiltersViewport();
      this._teardownInfinite();
      if (this._filtersPortaled || this._filtersScrollLocked) {
        this._setFiltersOpen(false);
      }
    }

    _onSortViewportChange() {
      this._syncSortControls();
    }

    /**
     * Desktop uses sidebar sort radios; mobile/tablet keeps the toolbar select.
     * Disable the hidden control so FormData only submits one sort_by.
     */
    _syncSortControls() {
      const desktop = this._sortMedia?.matches ?? window.matchMedia("(min-width: 990px)").matches;
      const select = this.querySelector("[data-collection-sort-select]");
      const form = this._getFilterForm();
      const radios = form?.querySelectorAll("[data-collection-sort-sidebar] input[name='sort_by']") || [];

      if (!select || radios.length === 0) return;

      if (desktop) {
        const current = select.value;
        radios.forEach((radio) => {
          radio.disabled = false;
          if (radio.value === current) radio.checked = true;
        });
        select.disabled = true;
      } else {
        const checked = form.querySelector("[data-collection-sort-sidebar] input[name='sort_by']:checked");
        if (checked) select.value = checked.value;
        radios.forEach((radio) => {
          radio.disabled = true;
        });
        select.disabled = false;
      }
    }

    _onPanelInput(event) {
      if (event.target.matches('[data-collection-filter-form] input[type="number"]')) {
        this._onDebouncedInput(event);
      }
    }

    _getFilterForm() {
      return this._filtersPanel || this.querySelector("[data-collection-filter-form]");
    }

    _getFilterOverlay() {
      return this._filtersOverlay || this.querySelector("[data-filters-overlay]");
    }

    _bindPortaledListeners(panel, overlay) {
      if (panel.dataset.portalListeners === "1") return;
      panel.addEventListener("change", this._onChange);
      panel.addEventListener("input", this._onPanelInput);
      panel.addEventListener("click", this._onClick);
      overlay?.addEventListener("click", this._onClick);
      panel.dataset.portalListeners = "1";
    }

    _unbindPortaledListeners(panel, overlay) {
      if (!panel || panel.dataset.portalListeners !== "1") return;
      panel.removeEventListener("change", this._onChange);
      panel.removeEventListener("input", this._onPanelInput);
      panel.removeEventListener("click", this._onClick);
      overlay?.removeEventListener("click", this._onClick);
      delete panel.dataset.portalListeners;
    }

    _syncFiltersViewportHeight(panel) {
      if (!panel) return;
      const height = Math.round(
        window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight
      );
      if (height > 0) {
        panel.style.setProperty("--collection-filters-vh", `${height}px`);
      }
    }

    _onFiltersViewportChange() {
      const panel = this._getFilterForm();
      if (panel?.classList.contains("is-open")) {
        this._syncFiltersViewportHeight(panel);
      }
    }

    _bindFiltersViewport() {
      if (this._filtersViewportBound) return;
      window.addEventListener("resize", this._onFiltersViewportChange);
      window.visualViewport?.addEventListener("resize", this._onFiltersViewportChange);
      window.visualViewport?.addEventListener("scroll", this._onFiltersViewportChange);
      this._filtersViewportBound = true;
    }

    _unbindFiltersViewport() {
      if (!this._filtersViewportBound) return;
      window.removeEventListener("resize", this._onFiltersViewportChange);
      window.visualViewport?.removeEventListener("resize", this._onFiltersViewportChange);
      window.visualViewport?.removeEventListener("scroll", this._onFiltersViewportChange);
      this._filtersViewportBound = false;
    }

    _applyPortaledColorScheme(panel) {
      if (!panel) return;
      const schemeHost = this.closest("[class*='colorGroup--']");
      if (!schemeHost) return;
      const schemeClass = [...schemeHost.classList].find((name) => name.startsWith("colorGroup--"));
      if (!schemeClass) return;
      if (this._portaledSchemeClass && this._portaledSchemeClass !== schemeClass) {
        panel.classList.remove(this._portaledSchemeClass);
      }
      panel.classList.add(schemeClass);
      this._portaledSchemeClass = schemeClass;
    }

    /**
     * iOS Safari: fixed drawers nested under Swiper/transform ancestors
     * fail to paint opaque backgrounds. Move overlay+panel to body while open
     * (same stacking model as cart-drawer).
     */
    _portalFilters(open) {
      const panel = this._getFilterForm();
      const overlay = this._getFilterOverlay();
      if (!panel) return;

      this._filtersPanel = panel;
      if (overlay) this._filtersOverlay = overlay;

      if (open && !this._filtersPortaled) {
        if (!this._filtersPlaceholder) {
          this._filtersPlaceholder = document.createComment("collection-filters-anchor");
        }
        if (overlay?.parentNode) {
          overlay.parentNode.insertBefore(this._filtersPlaceholder, overlay);
        } else {
          panel.parentNode?.insertBefore(this._filtersPlaceholder, panel);
        }

        this._applyPortaledColorScheme(panel);
        document.body.appendChild(overlay || panel);
        if (overlay) document.body.appendChild(panel);
        this._bindPortaledListeners(panel, overlay);
        this._filtersPortaled = true;
        return;
      }

      if (!open && this._filtersPortaled && this._filtersPlaceholder?.parentNode) {
        this._unbindPortaledListeners(panel, overlay);
        const parent = this._filtersPlaceholder.parentNode;
        if (overlay) parent.insertBefore(overlay, this._filtersPlaceholder);
        parent.insertBefore(panel, this._filtersPlaceholder);
        this._filtersPlaceholder.remove();
        this._filtersPlaceholder = null;
        this._filtersPortaled = false;
        if (this._portaledSchemeClass) {
          panel.classList.remove(this._portaledSchemeClass);
          this._portaledSchemeClass = null;
        }
        panel.style.removeProperty("--collection-filters-vh");
      }
    }

    _onPopState(event) {
      const searchParams = event.state?.searchParams ?? CollectionFilters.searchParamsInitial;
      if (searchParams === CollectionFilters.searchParamsPrev) return;

      document.querySelectorAll("collection-filters").forEach((element) => {
        element.renderPage(searchParams, false);
      });
    }

    _onChange(event) {
      const target = event.target;
      if (target.matches("[data-collection-filter-form] input[type='number']")) return;

      if (
        target.matches("[data-collection-sort-select]") ||
        target.closest("[data-collection-filter-form]")
      ) {
        event.preventDefault();
        this.renderPage(this.createSearchParams(), true);
      }
    }

    _onClick(event) {
      if (event.target.closest("[data-filters-open]")) {
        this._setFiltersOpen(true);
        return;
      }

      if (event.target.closest("[data-filters-close]") || event.target.closest("[data-filters-overlay]")) {
        this._setFiltersOpen(false);
        return;
      }

      const clearLink = event.target.closest("[data-collection-clear-filters]");
      if (clearLink) {
        event.preventDefault();
        const query = clearLink.href.includes("?")
          ? clearLink.href.slice(clearLink.href.indexOf("?") + 1)
          : "";
        this.renderPage(query, true);
        return;
      }

      if (event.target.closest("[data-collection-load-more-btn]")) {
        event.preventDefault();
        this._loadNextPage();
        return;
      }

      const pageLink = event.target.closest("[data-collection-pagination] a");
      if (pageLink) {
        event.preventDefault();
        const query = pageLink.href.includes("?")
          ? pageLink.href.slice(pageLink.href.indexOf("?") + 1)
          : "";
        this.renderPage(query, true);
        this.querySelector("[data-collection-results]")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    _setupPaginationMode() {
      this.paginationMode = this.dataset.paginationMode || "pagination";
      this._teardownInfinite();
      if (this.paginationMode === "infinite") {
        this._bindInfinite();
      }
    }

    _bindInfinite() {
      const sentinel = this.querySelector("[data-collection-infinite-sentinel]");
      if (!sentinel || typeof IntersectionObserver === "undefined") return;

      this._infiniteObserver = new IntersectionObserver(this._onInfiniteIntersect, {
        root: null,
        rootMargin: "280px 0px",
        threshold: 0,
      });
      this._infiniteObserver.observe(sentinel);
    }

    _teardownInfinite() {
      this._infiniteObserver?.disconnect();
      this._infiniteObserver = null;
    }

    _onInfiniteIntersect(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this._loadNextPage();
    }

    _searchParamsFromHref(href) {
      try {
        return new URL(href, window.location.origin).searchParams.toString();
      } catch (error) {
        if (!href.includes("?")) return "";
        return href.slice(href.indexOf("?") + 1);
      }
    }

    _setLoadMoreBusy(busy) {
      const button = this.querySelector("[data-collection-load-more-btn]");
      const label = this.querySelector("[data-collection-load-more-label]");
      const loading = this.querySelector("[data-collection-load-more-loading]");
      const status = this.querySelector("[data-collection-infinite-status]");

      if (button) {
        button.disabled = busy;
        button.setAttribute("aria-busy", busy ? "true" : "false");
      }
      if (label) label.hidden = busy;
      if (loading) loading.hidden = !busy;
      if (status) status.hidden = !busy;
    }

    async _loadNextPage() {
      if (this.paginationMode === "pagination" || this._loadingMore) return;

      const nextLink = this.querySelector("[data-collection-next]");
      if (!nextLink?.href) {
        this._teardownInfinite();
        return;
      }

      this._loadingMore = true;
      this._setLoadMoreBusy(true);

      try {
        const searchParams = this._searchParamsFromHref(nextLink.href);
        const url = `${window.location.pathname}?section_id=${this.sectionId}${
          searchParams ? `&${searchParams}` : ""
        }`;
        const html = await this._fetchSection(url);
        this._appendFromHtml(html);
      } catch (error) {
        console.error("[collection-filters]", error);
      } finally {
        this._loadingMore = false;
        this._setLoadMoreBusy(false);
        this._setupPaginationMode();
      }
    }

    _appendFromHtml(html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const source =
        doc.querySelector("collection-filters") ||
        doc.querySelector(`#shopify-section-${this.sectionId}`) ||
        doc.body;

      const grid = this.querySelector("[data-collection-grid]");
      const newGrid = source.querySelector("[data-collection-grid]");
      if (grid && newGrid) {
        const fragment = document.createDocumentFragment();
        while (newGrid.firstElementChild) {
          fragment.appendChild(newGrid.firstElementChild);
        }
        grid.appendChild(fragment);
      }

      const pager = this.querySelector("[data-collection-pager]");
      const newPager = source.querySelector("[data-collection-pager]");
      if (pager && newPager) {
        pager.replaceWith(newPager);
      } else if (pager && !newPager) {
        pager.remove();
      }

      if (typeof initScrollAnimations === "function") {
        initScrollAnimations(this);
      }
    }

    _setFiltersOpen(open) {
      const panel = this._getFilterForm();
      const overlay = this._getFilterOverlay();
      if (!panel) return;

      if (open) {
        this._portalFilters(true);
        this._syncFiltersViewportHeight(panel);
        this._bindFiltersViewport();
      }

      panel.classList.toggle("is-open", open);
      document.body.classList.toggle("collection-filters-locked", open);

      if (overlay) {
        overlay.classList.toggle("is-visible", open);
        overlay.setAttribute("aria-hidden", open ? "false" : "true");
      }

      // iOS: lock must target the filter scroller (panel itself scrolls)
      if (window.themeBodyScrollLock) {
        if (open) {
          this._filtersScrollY = window.scrollY || 0;
          if (!this._filtersScrollLocked) {
            window.themeBodyScrollLock.lock(panel);
          }
        } else if (this._filtersScrollLocked) {
          window.themeBodyScrollLock.unlock(panel);
          if (typeof this._filtersScrollY === "number") {
            window.scrollTo(0, this._filtersScrollY);
          }
        }
        this._filtersScrollLocked = open;
      } else if (open) {
        this._filtersScrollY = window.scrollY || 0;
        document.body.style.top = `-${this._filtersScrollY}px`;
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
      } else {
        document.body.style.top = "";
        document.body.style.position = "";
        document.body.style.width = "";
        if (typeof this._filtersScrollY === "number") {
          window.scrollTo(0, this._filtersScrollY);
        }
      }

      if (!open) {
        this._unbindFiltersViewport();
        this._portalFilters(false);
      }
    }

    createSearchParams() {
      const form = this._getFilterForm();
      const sort = this.querySelector("[data-collection-sort-select]");
      const params = new URLSearchParams();

      if (form) {
        new FormData(form).forEach((value, key) => {
          if (value !== "") params.append(key, value);
        });
      }

      if (sort && !sort.disabled && sort.value) {
        params.set("sort_by", sort.value);
      }

      return params.toString();
    }

    async renderPage(searchParams, updateURL = true) {
      CollectionFilters.searchParamsPrev = searchParams;
      const url = `${window.location.pathname}?section_id=${this.sectionId}${
        searchParams ? `&${searchParams}` : ""
      }`;

      const panel = this._getFilterForm();
      const wasDrawerOpen = panel?.classList.contains("is-open");

      this._teardownInfinite();
      this.classList.add("is-loading");
      this.querySelector("[data-collection-results]")?.setAttribute("aria-busy", "true");

      try {
        const cached = CollectionFilters.filterData.find((entry) => entry.url === url);
        const html = cached ? cached.html : await this._fetchSection(url);
        if (!cached) CollectionFilters.filterData.push({ url, html });
        this._renderFromHtml(html, wasDrawerOpen);
        if (updateURL) this._updateURL(searchParams);
      } catch (error) {
        console.error("[collection-filters]", error);
      } finally {
        this.classList.remove("is-loading");
        this.querySelector("[data-collection-results]")?.removeAttribute("aria-busy");
      }
    }

    async _fetchSection(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Section fetch failed (${response.status})`);
      return response.text();
    }

    _renderFromHtml(html, wasDrawerOpen) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const source =
        doc.querySelector("collection-filters") ||
        doc.querySelector(`#shopify-section-${this.sectionId}`) ||
        doc.body;

      const formTarget = this._getFilterForm();
      const map = {
        "[data-collection-toolbar]": this.querySelector("[data-collection-toolbar]"),
        "[data-collection-results]": this.querySelector("[data-collection-results]"),
        "[data-collection-filter-form]": formTarget,
      };

      Object.entries(map).forEach(([selector, target]) => {
        if (!target) return;
        const replacement = source.querySelector(selector);
        if (!replacement) return;
        target.innerHTML = replacement.innerHTML;
      });

      this._syncSortControls();
      this._setupPaginationMode();

      if (wasDrawerOpen) {
        this._setFiltersOpen(true);
      }

      if (typeof initScrollAnimations === "function") {
        initScrollAnimations(this);
      }
    }

    _updateURL(searchParams) {
      history.pushState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams ? `?${searchParams}` : ""}`
      );
    }
  }

  customElements.define("collection-filters", CollectionFilters);
})();
